import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getFacultyAssignmentInterventions,
  getNotificationCounts,
  getStudentAssignmentAttention,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../services/notifications.api';

export const notificationKeys = {
  all: ['notifications'],
  list: (params) => ['notifications', 'list', params],
  counts: ['notifications', 'counts'],
  studentAttention: (params) => ['notifications', 'student-attention', params],
  facultyIntervention: (params) => ['notifications', 'faculty-intervention', params],
};

export function useNotificationCounts({ refetchInterval = 60_000, enabled = true } = {}) {
  return useQuery({
    queryKey: notificationKeys.counts,
    queryFn: getNotificationCounts,
    staleTime: 30_000,
    refetchInterval: refetchInterval > 0 ? refetchInterval : false,
    enabled,
    select: (data) => data?.unreadCount ?? 0,
  });
}

export function useNotifications(params = { page: 1, limit: 20 }, { enabled = true } = {}) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => listNotifications(params),
    staleTime: 30_000,
    enabled,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useStudentAssignmentAttention(params = { limit: 5 }, options = {}) {
  return useQuery({
    queryKey: notificationKeys.studentAttention(params),
    queryFn: () => getStudentAssignmentAttention(params),
    staleTime: 60_000,
    ...options,
  });
}

export function useFacultyAssignmentInterventions(params = { limit: 5 }, options = {}) {
  return useQuery({
    queryKey: notificationKeys.facultyIntervention(params),
    queryFn: () => getFacultyAssignmentInterventions(params),
    staleTime: 60_000,
    ...options,
  });
}
